import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { mobileApi } from '../../services/api';
import { enqueueDefect } from '../../services/defectQueue';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import {
  QrCode, Search, Camera, MapPin, AlertTriangle, ChevronRight, Loader2, X,
} from 'lucide-react';
import { toast } from 'sonner';

const DRAFT_KEY = 'mobile_defect_draft';

const SEV = [
  { key: 'low', label: 'Low', dot: '#8A857A', fill: '#F1EEE6', border: '#8A857A', ink: '#59554D' },
  { key: 'medium', label: 'Medium', dot: '#E0B800', fill: '#FFF7D1', border: '#E0B800', ink: '#6B5600' },
  { key: 'critical', label: 'Critical', dot: '#C23A00', fill: '#FFE1E1', border: '#A81616', ink: '#8E1212' },
];

const FOCUS =
  'outline-none focus:border-[#1A1A1A] focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]';

const assetLine = (a) =>
  [[a.make, a.model].filter(Boolean).join(' '), a.year, a.location]
    .filter(Boolean)
    .join(' · ');

const MobileReportDefect = () => {
  const navigate = useNavigate();
  const { getToken } = useMobileAuth();
  const [params] = useSearchParams();

  const [step, setStep] = useState('select'); // 'select' | 'form'
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState({ matches: [], total: 0 });
  const [searching, setSearching] = useState(false);
  const [recent, setRecent] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [severity, setSeverity] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [location, setLocation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // recent assets
  useEffect(() => {
    try {
      setRecent(JSON.parse(localStorage.getItem('mobile_recent_scans') || '[]').slice(0, 5));
    } catch { /* noop */ }
  }, []);

  // restore draft (once), unless a scanned asset is passed in the URL
  useEffect(() => {
    const assetParam = params.get('asset');
    if (assetParam) {
      (async () => {
        try {
          const res = await mobileApi.getAsset(assetParam, getToken());
          const a = res.data;
          selectAsset({
            id: a.id, unit_number: a.unit_number, name: a.name, serial_number: a.serial_number,
            make: a.make, model: a.model, year: a.year, location: a.location,
            open_defects_count: (a.open_defects || []).length,
          }, false);
          setStep('form');
        } catch { /* noop */ }
      })();
      return;
    }
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      if (d) {
        setTitle(d.title || '');
        setDetail(d.detail || '');
        setSeverity(d.severity || null);
        setPhotos(d.photos || []);
        if (d.selectedAsset) { setSelectedAsset(d.selectedAsset); setStep('form'); }
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // persist draft on every change (survives lock / backgrounded tab)
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ title, detail, severity, photos, selectedAsset }));
  }, [title, detail, severity, photos, selectedAsset]);

  // capture GPS when entering the form
  useEffect(() => {
    if (step === 'form' && !location) captureLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const clearDraft = () => localStorage.removeItem(DRAFT_KEY);

  const captureLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        captured_at: new Date().toISOString(),
      }),
      () => {},
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const runSearch = async (q) => {
    setSearchQuery(q);
    if (q.trim().length < 1) { setResults({ matches: [], total: results.total }); return; }
    setSearching(true);
    try {
      const res = await mobileApi.searchAssets(q, getToken());
      setResults(res.data || { matches: [], total: 0 });
    } catch { setResults({ matches: [], total: 0 }); }
    finally { setSearching(false); }
  };

  const selectAsset = (asset, goForm = true) => {
    setSelectedAsset(asset);
    const updated = [
      { id: asset.id, unit_number: asset.unit_number, name: asset.name, location: asset.location, time: new Date().toISOString() },
      ...recent.filter((s) => s.id !== asset.id),
    ].slice(0, 5);
    localStorage.setItem('mobile_recent_scans', JSON.stringify(updated));
    setRecent(updated);
    if (goForm) setStep('form');
  };

  const handlePhotos = (e) => {
    Array.from(e.target.files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => setPhotos((p) => [...p, ev.target.result]);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const ready = title.trim().length > 0 && !!severity;
  const reason = !title.trim() && !severity
    ? 'Say what is wrong and pick a severity'
    : !title.trim() ? 'Say what is wrong' : 'Pick a severity';

  const submit = async () => {
    const payload = {
      asset_id: selectedAsset.id,
      title: title.trim(),
      description: detail.trim(),
      severity,
      photos,
      location,
    };
    const queueOffline = () => {
      enqueueDefect(payload);
      clearDraft();
      toast.success('Saved offline — it will send when you have signal');
      navigate('/mobile/defects');
    };
    if (!navigator.onLine) { queueOffline(); return; }
    setSubmitting(true);
    try {
      const res = await mobileApi.reportDefect(payload, getToken());
      clearDraft();
      toast.success(res.data?.asset_out_of_service
        ? 'Critical defect logged — asset taken out of service'
        : 'Defect reported');
      navigate('/mobile/defects');
    } catch (e) {
      if (!e.response) { queueOffline(); }
      else toast.error(e.response?.data?.detail || 'Failed to report defect');
    } finally {
      setSubmitting(false);
    }
  };

  // ── SELECT ASSET (2a) ───────────────────────────────────────────────────
  if (step === 'select') {
    const hasQuery = searchQuery.trim().length > 0;
    const matches = results.matches || [];
    return (
      <div className="min-h-screen bg-[var(--surface-page)]" data-testid="report-defect-asset-select">
        <MobileHeader title="Select asset" showBack />
        <div className="px-4 py-4">
          {/* Scan — primary */}
          <button
            onClick={() => navigate('/mobile/scan?return=defect')}
            data-testid="rd-scan-btn"
            className="w-full flex items-center justify-center gap-3 bg-[var(--volt)] text-black rounded-[13px] active:scale-[0.99] transition-transform"
            style={{ height: 62, border: '1px solid var(--volt-edge)', fontWeight: 800 }}
          >
            <QrCode className="w-6 h-6" />
            <span className="text-[16.5px]">Scan asset QR code</span>
          </button>

          {/* divider */}
          <div className="flex items-center gap-3 my-4">
            <div className="h-px flex-1 bg-[var(--border)]" />
            <span className="text-[12px] text-[var(--ink-tertiary)]">or find it by name</span>
            <div className="h-px flex-1 bg-[var(--border)]" />
          </div>

          {/* search */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ink-tertiary)]" />
            <input
              value={searchQuery}
              onChange={(e) => runSearch(e.target.value)}
              placeholder="Unit #, name or serial"
              autoFocus
              data-testid="asset-search-input"
              className={`w-full h-[54px] rounded-[12px] bg-white border border-[var(--border-strong)] pl-10 pr-4 text-[16px] text-[var(--ink-primary)] ${FOCUS}`}
            />
          </div>

          {searching && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-[var(--volt-icon)] animate-spin" />
            </div>
          )}

          {/* matches */}
          {hasQuery && !searching && (
            <>
              <div className="flex items-center justify-between mt-5 mb-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.11em]" style={{ color: '#6E6A61' }}>Matches</span>
                <span className="font-mono text-[11px] text-[var(--ink-tertiary)]">{matches.length} of {results.total} assets</span>
              </div>
              {matches.map((a) => (
                <button
                  key={a.id}
                  onClick={() => selectAsset(a)}
                  data-testid={`asset-match-${a.id}`}
                  className="w-full text-left bg-white border border-[var(--border)] rounded-[13px] px-4 py-3 mb-2 flex items-center justify-between"
                  style={{ boxShadow: 'var(--elev-raised)' }}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[14px] font-bold text-[var(--ink-primary)]">{a.unit_number || a.name}</span>
                      {a.open_defects_count > 0 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[11px] font-semibold border bg-[var(--overdue-bg)] text-[var(--overdue-ink)] border-[var(--overdue-edge)]">
                          {a.open_defects_count} open
                        </span>
                      )}
                    </div>
                    {a.serial_number && <div className="font-mono text-[11.5px] text-[var(--ink-tertiary)] mt-0.5 truncate">{a.serial_number}</div>}
                    {a.location && <div className="text-[12.5px] text-[var(--ink-secondary)] mt-0.5 truncate">{a.location}</div>}
                  </div>
                  <ChevronRight className="w-5 h-5 text-[var(--ink-tertiary)] shrink-0" />
                </button>
              ))}
              {matches.length === 0 && (
                <p className="text-[14px] text-[var(--ink-secondary)] py-3">No assets match “{searchQuery}”</p>
              )}
            </>
          )}

          {/* recent (before typing) */}
          {!hasQuery && recent.length > 0 && (
            <>
              <div className="mt-5 mb-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.11em]" style={{ color: '#6E6A61' }}>Recent</span>
              </div>
              {recent.map((r) => (
                <button
                  key={r.id}
                  onClick={() => selectAsset({ id: r.id, unit_number: r.unit_number, name: r.name, location: r.location }, true)}
                  data-testid={`asset-recent-${r.id}`}
                  className="w-full text-left bg-white border border-[var(--border)] rounded-[13px] px-4 py-2.5 mb-2 flex items-center justify-between"
                  style={{ boxShadow: 'var(--elev-raised)' }}
                >
                  <div className="min-w-0">
                    <div className="font-mono text-[14px] font-bold text-[var(--ink-primary)]">{r.unit_number || r.name}</div>
                    {r.location && <div className="text-[12.5px] text-[var(--ink-secondary)] mt-0.5 truncate">{r.location}</div>}
                  </div>
                  <ChevronRight className="w-5 h-5 text-[var(--ink-tertiary)] shrink-0" />
                </button>
              ))}
            </>
          )}

          {/* asset not listed */}
          <button
            onClick={() => toast.info('Ask your supervisor to add this asset, then try again.')}
            data-testid="asset-not-listed-btn"
            className="w-full h-11 mt-2 text-[13px] font-semibold text-[var(--volt-ink)]"
          >
            Asset not listed
          </button>
        </div>
      </div>
    );
  }

  // ── REPORT DEFECT (2b / 2c) ─────────────────────────────────────────────
  const a = selectedAsset || {};
  const openCount = a.open_defects_count || 0;
  const assetId = a.unit_number || a.name || 'this asset';
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;

  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-40" data-testid="report-defect-form">
      <MobileHeader title="Report defect" showBack onBack={() => setStep('select')} />

      <div className="px-4 py-4 space-y-5">
        {/* asset card */}
        <MobileCard padding="normal">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[14px] font-bold text-[var(--ink-primary)]">{assetId}</div>
              {assetLine(a) && <div className="text-[12.5px] text-[var(--ink-secondary)] mt-0.5 truncate">{assetLine(a)}</div>}
            </div>
            <button
              onClick={() => setStep('select')}
              data-testid="rd-change-asset"
              className="text-[13px] font-semibold text-[var(--volt-ink)] shrink-0"
            >
              Change
            </button>
          </div>
          {openCount > 0 && (
            <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-[var(--divider)]">
              <div className="flex items-center gap-2 min-w-0">
                <span className="shrink-0 rounded-full" style={{ width: 8, height: 8, background: 'var(--overdue)' }} />
                <span className="text-[13px] text-[var(--ink-secondary)] truncate">
                  <b className="text-[var(--ink-primary)]">{openCount} open defect{openCount === 1 ? '' : 's'}</b> already on this asset
                </span>
              </div>
              <button
                onClick={() => navigate(`/mobile/assets/${a.id}`)}
                data-testid="rd-view-open-defects"
                className="text-[13px] font-semibold text-[var(--volt-ink)] shrink-0"
              >
                View
              </button>
            </div>
          )}
        </MobileCard>

        {/* what is wrong */}
        <div>
          <label className="block text-[13px] font-semibold text-[var(--ink-secondary)] mb-2">What is wrong?</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short description of the fault"
            data-testid="defect-title-input"
            className={`w-full h-[54px] rounded-[12px] bg-white border border-[var(--border-strong)] px-4 text-[16px] text-[var(--ink-primary)] ${FOCUS}`}
          />
        </div>

        {/* severity */}
        <div>
          <label className="block text-[13px] font-semibold text-[var(--ink-secondary)] mb-2">Severity</label>
          <div className="grid grid-cols-3 gap-2">
            {SEV.map((s) => {
              const sel = severity === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setSeverity(s.key)}
                  data-testid={`severity-${s.key}`}
                  className="h-14 rounded-[12px] flex flex-col items-center justify-center gap-1 transition-colors"
                  style={sel
                    ? { background: s.fill, border: `2px solid ${s.border}`, color: s.ink }
                    : { background: '#FFFFFF', border: '1px solid #C4BFB3', color: '#1A1A1A' }}
                >
                  <span className="rounded-full" style={{ width: 9, height: 9, background: s.dot }} />
                  <span className="text-[13px] font-medium">{s.label}</span>
                </button>
              );
            })}
          </div>

          {severity === 'critical' && (
            <div className="mt-3 rounded-[12px] p-3 flex gap-2" style={{ background: '#FFE1E1', border: '1px solid #EBAFAF' }}>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#8E1212' }} />
              <p className="text-[12.5px] leading-snug" style={{ color: '#8E1212' }}>
                <b>Critical takes {assetId} out of service.</b> It cannot be booked out until an inspector clears it, and the workshop supervisor is notified now.
              </p>
            </div>
          )}
        </div>

        {/* detail */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[13px] font-semibold text-[var(--ink-secondary)]">Detail</label>
            <span className="text-[12px] text-[var(--ink-tertiary)]">Optional</span>
          </div>
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="What you saw, heard or measured"
            data-testid="defect-detail-input"
            className={`w-full min-h-[84px] rounded-[12px] bg-white border border-[var(--border-strong)] px-4 py-3 text-[16px] leading-snug text-[var(--ink-primary)] ${FOCUS}`}
          />
        </div>

        {/* photos */}
        <div>
          {photos.length === 0 ? (
            <label
              data-testid="rd-add-photo"
              className="w-full h-[52px] rounded-[13px] bg-white flex items-center justify-center gap-2 cursor-pointer active:scale-[0.99] transition-transform"
              style={{ border: '1.5px solid #1A1A1A' }}
            >
              <Camera className="w-[18px] h-[18px] text-[var(--ink-primary)]" />
              <span className="text-[14px] font-semibold text-[var(--ink-primary)]">Add photo</span>
              <input type="file" accept="image/*" capture="environment" multiple onChange={handlePhotos} className="hidden" />
            </label>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              {photos.map((p, i) => (
                <div key={i} className="relative" style={{ width: 64, height: 64 }}>
                  <img src={p} alt="" className="w-full h-full object-cover rounded-[12px]" />
                  <button
                    onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white flex items-center justify-center"
                    style={{ border: '1px solid var(--border-strong)' }}
                  >
                    <X className="w-3.5 h-3.5 text-[var(--ink-primary)]" />
                  </button>
                </div>
              ))}
              <label
                className="flex items-center justify-center rounded-[12px] cursor-pointer"
                style={{ width: 64, height: 64, border: '1.5px dashed var(--border-strong)' }}
                data-testid="rd-add-photo-more"
              >
                <Camera className="w-5 h-5 text-[var(--ink-tertiary)]" />
                <input type="file" accept="image/*" capture="environment" multiple onChange={handlePhotos} className="hidden" />
              </label>
              <span className="text-[13px] text-[var(--ink-secondary)]">{photos.length} photo{photos.length === 1 ? '' : 's'} attached</span>
            </div>
          )}
        </div>

        {/* location */}
        {location && (
          <div className="w-full bg-white rounded-[13px] px-4 py-2.5 flex items-center justify-between" style={{ border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 min-w-0">
              <MapPin className="w-4 h-4 text-[var(--volt-icon)] shrink-0" />
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-[var(--ink-primary)] truncate">{a.location || 'Current location'}</div>
                <div className="font-mono text-[10.5px] text-[var(--ink-tertiary)]">
                  ±{Math.round(location.accuracy || 0)} m · captured {new Date(location.captured_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
            <button onClick={captureLocation} data-testid="rd-redo-location" className="text-[13px] font-semibold text-[var(--volt-ink)] shrink-0">Redo</button>
          </div>
        )}
      </div>

      {/* fixed bottom bar — consequence, then button */}
      <div className="fixed bottom-0 left-0 right-0 px-4 pt-2.5 pb-4 bg-[var(--surface-page)]" style={{ boxShadow: 'var(--elev-floating)' }}>
        <p className="text-[12.5px] text-[var(--ink-secondary)] leading-snug mb-2.5" data-testid="rd-consequence">
          Creates a {severity === 'critical' ? <b className="text-[var(--ink-primary)]">critical </b> : ''}defect against <b className="text-[var(--ink-primary)]">{assetId}</b>
          {severity === 'critical' ? ', takes it out of service and notifies the workshop supervisor.' : ' and notifies the workshop supervisor.'}
          {offline && <> <b className="text-[var(--ink-primary)]">You are offline</b> — this is saved on your phone and sends when signal returns.</>}
        </p>
        {ready ? (
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="submit-defect-btn"
            className="w-full rounded-[13px] bg-[var(--volt)] text-black flex items-center justify-center active:scale-[0.99] transition-transform"
            style={{ height: 58, fontWeight: 800, border: '1px solid var(--volt-edge)' }}
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'SUBMIT DEFECT'}
          </button>
        ) : (
          <>
            <div
              className="w-full rounded-[13px] flex items-center justify-center"
              data-testid="submit-defect-disabled"
              style={{ height: 58, background: '#E6E2D9', border: '1px solid #D3CEC2', color: '#A29D93', fontWeight: 800 }}
            >
              SUBMIT DEFECT
            </div>
            <p className="text-center text-[12.5px] mt-2" style={{ color: '#9A3200' }}>{reason}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default MobileReportDefect;
